import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  addDays,
  addMonths,
  addYears,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CalendarioEvento, CalendarioItemVirtual } from '@/types';

interface TimelineEvent {
  id: string;
  title: string;
  start: string;
  end?: string;
  allDay: boolean;
  backgroundColor: string;
  // `evento` ausente identifica um feriado ou um item virtual (ver `feriadoEvents`/
  // `projetoEvents`/`atividadeEvents` em Calendario.tsx) — nenhum dos dois é editável aqui.
  // `virtual` presente identifica Projeto/Atividade do ProjetoT — clicável, mas abre preview
  // read-only (`onVirtualClick`), nunca o modal de edição de evento real.
  // `empresaNome` só vem preenchido quando a pessoa tem mais de uma empresa na agenda única.
  extendedProps: { evento?: CalendarioEvento; isFeriado?: boolean; virtual?: CalendarioItemVirtual; empresaNome?: string };
}

type Granularidade = 'ano' | 'mes' | 'semana' | 'dia';

interface CalendarioTimelineProps {
  eventos: TimelineEvent[];
  onEventoClick: (evento: CalendarioEvento) => void;
  onVirtualClick?: (item: CalendarioItemVirtual) => void;
  // Controlada pelo mesmo cluster Ano/Mês/Semana/Dia do topo da página (não tem toggle próprio).
  granularidade: Granularidade;
}

// Mesma lógica de `toDateOnly`/`parseInputDateLocal` (EventoModal.tsx): extrai a data "nominal"
// via getters UTC e reconstrói como Date LOCAL, pra não perder um dia em fusos negativos.
function paraDataLocal(iso: string): Date {
  const d = new Date(iso);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

const LARGURA_COLUNA_LABEL = 220;
const ALTURA_LINHA = 34;
const DIAS_JANELA_DIA = 3;

export function CalendarioTimeline({ eventos, onEventoClick, onVirtualClick, granularidade }: CalendarioTimelineProps) {
  const [referencia, setReferencia] = useState(() => new Date());
  const hoje = new Date();

  // Ao trocar de escala (mesmo cluster do topo), volta a referência pra hoje — evita ficar "perdido"
  // num período distante ao alternar, ex: de Ano (2028) direto pra Dia sem passar por hoje.
  useEffect(() => {
    setReferencia(new Date());
  }, [granularidade]);

  const { periodoInicio, periodoFim, colunas, titulo, larguraColuna } = useMemo(() => {
    if (granularidade === 'dia') {
      const inicio = subDays(referencia, DIAS_JANELA_DIA);
      const fim = addDays(referencia, DIAS_JANELA_DIA);
      return {
        periodoInicio: inicio,
        periodoFim: fim,
        colunas: eachDayOfInterval({ start: inicio, end: fim }),
        titulo: `${format(inicio, 'd MMM', { locale: ptBR })} – ${format(fim, 'd MMM yyyy', { locale: ptBR })}`,
        larguraColuna: 80,
      };
    }
    if (granularidade === 'semana') {
      const inicio = startOfWeek(referencia, { weekStartsOn: 0 });
      const fim = endOfWeek(referencia, { weekStartsOn: 0 });
      return {
        periodoInicio: inicio,
        periodoFim: fim,
        colunas: eachDayOfInterval({ start: inicio, end: fim }),
        titulo: `${format(inicio, 'd MMM', { locale: ptBR })} – ${format(fim, 'd MMM yyyy', { locale: ptBR })}`,
        larguraColuna: 56,
      };
    }
    if (granularidade === 'ano') {
      const inicio = startOfYear(referencia);
      const fim = endOfYear(referencia);
      return {
        periodoInicio: inicio,
        periodoFim: fim,
        colunas: eachMonthOfInterval({ start: inicio, end: fim }),
        titulo: format(referencia, 'yyyy'),
        larguraColuna: 72,
      };
    }
    const inicio = startOfMonth(referencia);
    const fim = endOfMonth(referencia);
    return {
      periodoInicio: inicio,
      periodoFim: fim,
      colunas: eachDayOfInterval({ start: inicio, end: fim }),
      titulo: format(referencia, 'MMMM yyyy', { locale: ptBR }),
      larguraColuna: 32,
    };
  }, [granularidade, referencia]);

  const eventosNoPeriodo = useMemo(() => {
    return eventos
      .map((ev) => {
        const inicio = paraDataLocal(ev.start);
        const fimBruto = ev.end ? paraDataLocal(ev.end) : inicio;
        // `end` de evento dia-inteiro vem exclusivo (mesma convenção do FullCalendar já usada
        // nesta página) — volta 1 dia pra virar o último dia inclusive exibido na barra.
        const fim = ev.allDay && ev.end ? subDays(fimBruto, 1) : fimBruto;
        return { ev, inicio, fim: fim < inicio ? inicio : fim };
      })
      .filter(({ inicio, fim }) => fim >= periodoInicio && inicio <= periodoFim)
      .sort((a, b) => a.inicio.getTime() - b.inicio.getTime());
  }, [eventos, periodoInicio, periodoFim]);

  const diferencaNaEscala = (data: Date) =>
    granularidade === 'ano' ? differenceInCalendarMonths(data, periodoInicio) : differenceInCalendarDays(data, periodoInicio);

  const irParaAnterior = () => {
    if (granularidade === 'dia') setReferencia((r) => subDays(r, DIAS_JANELA_DIA * 2 + 1));
    else if (granularidade === 'semana') setReferencia((r) => subDays(r, 7));
    else if (granularidade === 'ano') setReferencia((r) => addYears(r, -1));
    else setReferencia((r) => addMonths(r, -1));
  };
  const irParaProximo = () => {
    if (granularidade === 'dia') setReferencia((r) => addDays(r, DIAS_JANELA_DIA * 2 + 1));
    else if (granularidade === 'semana') setReferencia((r) => addDays(r, 7));
    else if (granularidade === 'ano') setReferencia((r) => addYears(r, 1));
    else setReferencia((r) => addMonths(r, 1));
  };

  const gridTemplateColumns = `${LARGURA_COLUNA_LABEL}px repeat(${colunas.length}, minmax(${larguraColuna}px, 1fr))`;

  return (
    <div className="flex h-full flex-col rounded-lg border bg-card">
      <div className="flex items-center justify-between gap-2 border-b p-2 flex-wrap">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-7 w-7" onClick={irParaAnterior}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => setReferencia(new Date())}>
            Hoje
          </Button>
          <Button variant="outline" size="icon" className="h-7 w-7" onClick={irParaProximo}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="ml-1 text-sm font-medium capitalize">{titulo}</span>
        </div>
        <span className="text-xs text-muted-foreground">{eventosNoPeriodo.length} evento(s) no período</span>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {eventosNoPeriodo.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            Nenhum evento neste período.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns, gridAutoRows: ALTURA_LINHA }}>
            <div
              className="sticky left-0 top-0 z-20 flex items-center border-b border-r bg-card px-2 text-xs font-medium text-muted-foreground"
              style={{ gridColumn: 1, gridRow: 1 }}
            >
              Evento
            </div>
            {colunas.map((coluna, i) => (
              <div
                key={i}
                style={{ gridColumn: i + 2, gridRow: 1 }}
                className={cn(
                  'sticky top-0 z-10 border-b border-l bg-card py-1 text-center text-[10px] leading-tight',
                  granularidade !== 'ano' && (coluna.getDay() === 0 || coluna.getDay() === 6) && 'bg-muted/50',
                  granularidade === 'ano' ? isSameMonth(coluna, hoje) && 'bg-primary/10 font-semibold text-primary'
                    : isSameDay(coluna, hoje) && 'bg-primary/10 font-semibold text-primary',
                )}
              >
                {granularidade === 'ano' ? (
                  <div className="capitalize">{format(coluna, 'MMM', { locale: ptBR })}</div>
                ) : (
                  <>
                    <div>{format(coluna, 'd')}</div>
                    <div className="text-muted-foreground">{format(coluna, 'EEEEE', { locale: ptBR })}</div>
                  </>
                )}
              </div>
            ))}

            {eventosNoPeriodo.map(({ ev, inicio, fim }, rowIdx) => {
              const inicioClampado = inicio < periodoInicio ? periodoInicio : inicio;
              const fimClampado = fim > periodoFim ? periodoFim : fim;
              const startIdx = diferencaNaEscala(inicioClampado);
              const endIdx = diferencaNaEscala(fimClampado);
              const clicavel = !ev.extendedProps.isFeriado;
              const handleClick = () => {
                if (!clicavel) return;
                if (ev.extendedProps.virtual) onVirtualClick?.(ev.extendedProps.virtual);
                else onEventoClick(ev.extendedProps.evento!);
              };

              return (
                <Fragment key={ev.id}>
                  <div
                    style={{ gridColumn: 1, gridRow: rowIdx + 2 }}
                    className={cn(
                      'sticky left-0 z-10 flex items-center gap-1.5 border-r bg-card px-2 text-xs',
                      clicavel && 'cursor-pointer hover:bg-accent',
                    )}
                    onClick={handleClick}
                    title={ev.title}
                  >
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: ev.backgroundColor }} />
                    <span className="truncate">{ev.title}</span>
                    {ev.extendedProps.empresaNome && (
                      <span
                        className="ml-auto max-w-[40%] shrink-0 truncate rounded-full bg-muted px-1.5 text-[10px] text-muted-foreground"
                        title={ev.extendedProps.empresaNome}
                      >
                        {ev.extendedProps.empresaNome}
                      </span>
                    )}
                  </div>
                  {colunas.map((coluna, i) => (
                    <div
                      key={i}
                      style={{ gridColumn: i + 2, gridRow: rowIdx + 2 }}
                      className={cn(
                        'border-l',
                        granularidade !== 'ano' && (coluna.getDay() === 0 || coluna.getDay() === 6) && 'bg-muted/30',
                      )}
                    />
                  ))}
                  <div
                    style={{
                      gridColumn: `${startIdx + 2} / ${endIdx + 3}`,
                      gridRow: rowIdx + 2,
                      background: ev.backgroundColor,
                    }}
                    className={cn(
                      'mx-0.5 my-1 flex items-center truncate rounded-md px-2 text-[11px] font-medium text-white',
                      clicavel && 'cursor-pointer',
                    )}
                    onClick={handleClick}
                    title={ev.title}
                  >
                    {ev.title}
                  </div>
                </Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
